import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import Modal from "@/components/admin/Modal";
import { toast } from "sonner";

const ROLES = [
  ["store_manager", "Store Manager — manages products, stock, orders, media"],
  ["fulfillment_staff", "Fulfillment Staff — orders & packing slips only"],
  ["super_admin", "Super Admin — full agency control"],
  ["customer", "Customer"],
];

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState({ name: "", email: "", password: "", role: "store_manager" });

  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setF({ name: "", email: "", password: "", role: "store_manager" }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setF({ name: u.name, email: u.email, password: "", role: u.role }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/admin/users/${editing.id}`, { name: f.name, role: f.role, password: f.password || undefined });
      else await api.post("/admin/users", f);
      toast.success("Saved."); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (u) => { if (!window.confirm(`Delete ${u.email}?`)) return; try { await api.delete(`/admin/users/${u.id}`); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div data-testid="admin-users">
      <div className="flex justify-between items-center mb-8">
        <div><h1 className="font-heading text-4xl tracking-tight">Users & Roles</h1><p className="font-body text-sm text-ink/60 mt-1">Create and invite clients & staff with assigned roles.</p></div>
        <button onClick={openNew} className="adm-btn" data-testid="create-user-btn"><Plus size={14} /> Add User</button>
      </div>

      <div className="adm-card p-0 overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-cream"><tr className="font-body uppercase tracking-wide2 text-[0.6rem] text-ink/60">
            <th className="px-5 py-3">Name</th><th className="px-5 py-3">Email</th><th className="px-5 py-3">Role</th><th className="px-5 py-3"></th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-ink/5 font-body text-sm" data-testid={`user-row-${u.email}`}>
                <td className="px-5 py-3">{u.name}</td>
                <td className="px-5 py-3 text-ink/70">{u.email}</td>
                <td className="px-5 py-3"><span className="bg-warm/40 px-2 py-1 text-[0.6rem] uppercase tracking-wide2">{u.role.replace("_", " ")}</span></td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => openEdit(u)} className="icon-thin mr-3 text-ink/60 hover:text-ink" data-testid={`edit-user-${u.email}`}><Pencil size={15} /></button>
                  <button onClick={() => del(u)} className="icon-thin text-ink/60 hover:text-destructive"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit User" : "Add User"} testId="user-modal">
        <div className="space-y-4">
          <div><label className="adm-label">Name</label><input className="adm-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name-input" /></div>
          <div><label className="adm-label">Email</label><input className="adm-input" value={f.email} disabled={!!editing} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email-input" /></div>
          <div><label className="adm-label">{editing ? "New Password (optional)" : "Password"}</label><input className="adm-input" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-password-input" /></div>
          <div><label className="adm-label">Role</label>
            <select className="adm-input" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="user-role-select">
              {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <button onClick={save} className="adm-btn w-full" data-testid="user-save-btn">Save User</button>
        </div>
      </Modal>
    </div>
  );
}
